import { styled } from "goober";
import { useState, type ReactNode } from "react";
import { mediaTypeFromFileName } from "../../shared/fileMediaTypes";
import { formatBytes } from "../lib/formatBytes";
import { mediaTypeLabel } from "../lib/mediaTypeLabel";
import { CountBadge } from "./CountBadge";
import { Icon } from "./Icon";
import { PanelBackHeading } from "./PanelBackHeading";
import {
  ChatpadBody,
  ChatpadList,
  ChatpadListBadge,
  ChatpadListHeader,
  ChatpadListRow,
  ChatpadListScrollArea,
} from "./PersonalChats/ChatpadList";
import { iconForFileName } from "./PersonalChats/chatOutputs";
import { StepDetails } from "./StepDetails";
import { Text } from "./Text";

const Thumbnail = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
`;

export function FileListRow({
  name,
  folder = false,
  mediaType = mediaTypeFromFileName(name),
  sizeBytes,
  thumbnailUrl,
  trailing,
  onPress,
}: {
  name: string;
  folder?: boolean;
  mediaType?: string;
  sizeBytes?: number;
  thumbnailUrl?: string;
  trailing?: ReactNode;
  onPress: () => void;
}) {
  const [failedThumbnailUrl, setFailedThumbnailUrl] = useState<string>();
  const description = folder
    ? "Folder"
    : [mediaTypeLabel(mediaType), sizeBytes != null && formatBytes(sizeBytes)]
        .filter(Boolean)
        .join(" · ");
  const showThumbnail =
    !folder &&
    mediaType.startsWith("image/") &&
    thumbnailUrl &&
    thumbnailUrl !== failedThumbnailUrl;

  return (
    <ChatpadListRow
      title={name}
      description={description}
      badge={
        <ChatpadListBadge>
          {showThumbnail ? (
            <Thumbnail
              src={thumbnailUrl}
              alt=""
              crossOrigin="use-credentials"
              loading="lazy"
              decoding="async"
              onError={() => setFailedThumbnailUrl(thumbnailUrl)}
            />
          ) : (
            <Icon name={folder ? "folder" : iconForFileName(name)} size={16} />
          )}
        </ChatpadListBadge>
      }
      trailing={trailing}
      onPress={onPress}
    />
  );
}

export function FileList({
  title,
  count,
  onBack,
  backLabel = "Back to files",
  actions,
  showHeader = true,
  children,
}: {
  title: string;
  count?: number;
  onBack?: () => void;
  backLabel?: string;
  actions?: ReactNode;
  showHeader?: boolean;
  children: ReactNode;
}) {
  return (
    <StepDetails
      embedded
      heading={null}
      flushBody
      body={
        <ChatpadBody data-fill="true">
          <ChatpadList role="region" aria-label={title} data-fill="true">
            {showHeader && (
              <ChatpadListHeader
                style={{ paddingInline: onBack ? 8 : 16, gap: 8 }}
              >
                {onBack ? (
                  <PanelBackHeading
                    title={title}
                    backLabel={backLabel}
                    onBack={onBack}
                  />
                ) : (
                  <Text variant="text-small-md">{title}</Text>
                )}
                {count != null && (
                  <CountBadge>
                    <Text variant="text-smaller-md">{count}</Text>
                  </CountBadge>
                )}
                {actions && <div style={{ marginLeft: "auto" }}>{actions}</div>}
              </ChatpadListHeader>
            )}
            <ChatpadListScrollArea>{children}</ChatpadListScrollArea>
          </ChatpadList>
        </ChatpadBody>
      }
    />
  );
}
